



















import { computed, defineComponent, PropType, ref } from "@vue/composition-api";
import { ContentFile } from "@scrile/api-provider/dist/api/ContentFilesProvider";
import { ContentSet } from "@scrile/api-provider/dist/api/ContentSetProvider";
import { LikeSubjectType } from "@scrile/api-provider/dist/api/LikeProvider";
import { PaymentPackageEnum } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { ContentFeedTypeEnum } from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import { ContentFeed, ContentFeedType } from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import { EventPost } from "@scrile/api-provider/dist/projects/scrileConnect/EventPostProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import useMySubscriptionLevels from "@/hooks/useMySubscriptionLevels";
import usePaymentPackages from "@/hooks/usePaymentPackages";
import providers from "@/lib/api";
import BaseSpinner from "@/components/BaseSpinner";
import getIntegration from "@/components/ContentFeedItem";
import { FileUploadData, PostPriceData } from "@/components/PostCreate/types";

const contentFileAdapter = (file: ContentFile | FileUploadData): ContentFile | null => {
  if ("setId" in file) return file;
  if (!file.blobUrl) return null;
  return {
    id: file.id,
    mediaType: file.file.type,
    name: file.file.name,
    publicAccess: file.publicAccess,
    setId: "",
    size: file.file.size,
    width: 0,
    height: 0,
    sort: 0,
    title: file.file.name,
    uploadTime: "",
    urlPart: file.blobUrl,
    uuid: "0",
    processingStatus: "NEW",
    duration: 0,
    metadata: {},
    thumbnailType: file.thumbnailType ?? null,
  };
};

export default defineComponent({
  name: "ContentFeedPreview",
  components: { BaseSpinner },
  props: {
    postData: { type: Object as PropType<ContentSet>, required: true },
    files: { type: Array as PropType<Array<ContentFile | FileUploadData>>, required: true },
    priceData: { type: Object as PropType<PostPriceData>, required: true },
    type: { type: String as PropType<ContentFeedType>, required: true },
    eventData: { type: Object as PropType<EventPost | null> },
  },
  setup(props) {
    const { paymentPackages, getPaymentPackages } = usePaymentPackages([PaymentPackageEnum.FILESET]);
    const { mySubscriptionLevels, getMySubscriptionLevels } = useMySubscriptionLevels();
    const user = ref<UserPublicData>();
    const loading = ref(true);

    const details = computed(() => {
      switch (props.type) {
        case ContentFeedTypeEnum.EVENT:
          return {
            event: { ...props.eventData, status: "PUBLISHED" },
          };
        default:
          return null;
      }
    });

    const preview = computed<ContentFeed | null>(() => {
      if (!user.value) return null;
      const { published, cover, ...postData } = props.postData;
      return {
        ...postData,
        ...props.priceData,
        userId: postData.userId + "_preview",
        publishTime: props.postData.publishTime ?? new Date().toISOString(),
        files: props.files.map(f => contentFileAdapter(f)).filter(f => !!f) as ContentFile[],
        purchased: false,
        type: props.type,
        user: user.value,
        likes: {
          subject: { subjectId: props.postData.id, subjectType: LikeSubjectType.CONTENT_SET },
          count: 0,
          liked: false,
        },
        details: details.value,
      };
    });

    const component = computed(() => preview.value && getIntegration(preview.value));

    const getUser = async () => {
      const response = await providers.UserPublicDataProvider.search({ data: { id: [props.postData.userId] } });
      user.value = response.result[0];
    };

    const getData = async () => {
      await Promise.all([getPaymentPackages(), getMySubscriptionLevels(), getUser()]);
    };

    getData().finally(() => (loading.value = false));

    return { loading, component, paymentPackages, mySubscriptionLevels, preview };
  },
});
